import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { getProductOrdersTimeline } from "../../api/productApi";
import Loader from "../common/Loader";
import BookHistoryTimeline from "../common/BookHistoryTimeline";

export default function BookHistoryModal({ open, onClose, book, onAddDamage }) {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const navigate = useNavigate();

    const handleMemberClick = (memberId) => {
        onClose();
        navigate("/members", { state: { memberId } });
    };

    const handleOrderClick = (orderId) => {
        onClose();
        navigate(`/orders?id=${orderId}`);
    };

    const handleAddDamage = (item, initialData) => {
        if (onAddDamage) {
            onAddDamage(book, initialData);
        }
    };

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [timeline, setTimeline] = useState([]);
    const [product, setProduct] = useState(null);

    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState(null);

    useEffect(() => {
        if (book?.id) setPage(1);
    }, [book?.id]);

    useEffect(() => {
        if (!open) return;
        if (!book?.id) return;
        if (!token) return;

        let ignore = false;

        async function loadHistory() {
            try {
                setLoading(true);
                setError("");

                const res = await getProductOrdersTimeline({
                    productId: book.id,
                    token,
                    page,
                    perPage: 10,
                });

                if (ignore) return;

                if (!res?.success) {
                    setError("Book history not available");
                    setTimeline([]);
                    setProduct(null);
                    return;
                }

                setTimeline(Array.isArray(res.data) ? res.data : []);
                setProduct(res.product || null);
                setPagination(res.pagination || null);
            } catch (e) {
                if (!ignore) {
                    setError("Failed to load book history");
                    setTimeline([]);
                    setProduct(null);
                }
            } finally {
                if (!ignore) setLoading(false);
            }
        }

        loadHistory();

        return () => {
            ignore = true;
        };
    }, [open, book?.id, token, page]);

    if (!open) return null;

    const headerImg = product?.image || book?.img || "https://via.placeholder.com/120x180";
    const headerName = product?.name || book?.title || "-";
    const headerCredits = product?.price
        ? `${product.price} Credits`
        : book?.credits
            ? `${book.credits} Credits`
            : "-";

    const stock = product?.stock_status || book?.stock_status || "Available";
    const isOut = stock === "outofstock";

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-4">
            <div className="bg-white w-full max-w-6xl rounded-lg shadow-lg overflow-hidden">
                {/* HEADER */}
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <h2 className="text-3xl font-semibold">Book History</h2>

                    <button
                        onClick={onClose}
                        className="p-2 rounded hover:bg-gray-100"
                        title="Close"
                    >
                        <XMarkIcon className="h-6 w-6 text-gray-600" />
                    </button>
                </div>

                {/* BODY */}
                <div className="px-6 py-5 max-h-[78vh] overflow-y-auto">
                    {/* TOP STRIP */}
                    <div className="flex items-start gap-4 pb-5 border-b">
                        <div className="w-16 h-20 flex-shrink-0 overflow-hidden rounded border">
                            <img
                                src={headerImg}
                                alt={headerName}
                                className="w-full h-full object-cover"
                            />
                        </div>

                        <div className="space-y-1">
                            <h3 className="text-xl font-semibold">{headerName}</h3>
                            <p className="text-sm text-blue-600 font-medium">{headerCredits}</p>
                            <p className="text-sm text-gray-500">
                                {isOut ? "Out of stock" : "Available"}
                            </p>
                            <p className="text-sm text-gray-400">{book?.id}</p>
                        </div>
                    </div>

                    {/* LOADER */}
                    {loading && (
                        <div className="py-10 flex justify-center">
                            <Loader />
                        </div>
                    )}

                    {/* ERROR */}
                    {!loading && error && (
                        <div className="py-8 text-center text-red-500">{error}</div>
                    )}

                    {/* LIST */}
                    {!loading && !error && (
                        <div className="pt-4">
                            <BookHistoryTimeline
                                timeline={timeline}
                                loading={loading}
                                token={token}
                                onMemberClick={handleMemberClick}
                                onOrderClick={handleOrderClick}
                                onAddDamage={handleAddDamage}
                            />
                        </div>
                    )}

                    {/* PAGINATION */}
                    {pagination?.total_pages > 1 && (
                        <div className="flex items-center justify-between mt-6">
                            <button
                                disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                className={`px-4 py-2 rounded border text-sm ${page <= 1
                                    ? "text-gray-300 border-gray-200 cursor-not-allowed"
                                    : "text-gray-700 hover:bg-gray-50"
                                    }`}
                            >
                                Prev
                            </button>

                            <p className="text-sm text-gray-500">
                                Page <span className="font-semibold">{page}</span> of{" "}
                                <span className="font-semibold">{pagination.total_pages}</span>
                            </p>

                            <button
                                disabled={page >= pagination.total_pages}
                                onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                                className={`px-4 py-2 rounded border text-sm ${page >= pagination.total_pages
                                    ? "text-gray-300 border-gray-200 cursor-not-allowed"
                                    : "text-gray-700 hover:bg-gray-50"
                                    }`}
                            >
                                Next
                            </button>
                        </div>
                    )}

                    {/* FOOTER */}
                    <div className="pt-8 flex justify-end">
                        <button
                            onClick={onClose}
                            className="bg-black text-white px-10 py-3 rounded-md hover:bg-gray-900 transition"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
